import { EventEmitter } from 'eventemitter3';
import { ethers } from 'ethers';
import { createPublicClient, createWalletClient, http, custom } from 'viem';
import { mainnet, sepolia } from 'viem/chains';
import axios, { AxiosInstance } from 'axios';
import PQueue from 'p-queue';

import { 
  SDKConfig, 
  SDKEvents, 
  PlanType,
  ApiResponse,
  ApiError 
} from '../types';
import { RateLimiter } from '../middleware/rateLimiter';
import { TimeCapsuleService } from '../services/TimeCapsuleService';
import { PaymentService } from '../services/PaymentService';
import { LegalDocumentService } from '../services/LegalDocumentService';
import { AnalyticsService } from '../services/AnalyticsService';
import { IPFSService } from '../services/IPFSService';
import { CryptoService } from '../utils/crypto';
import { Logger } from '../utils/logger';

export class LegacyChainClient extends EventEmitter<SDKEvents> {
  private config: SDKConfig;
  private httpClient: AxiosInstance;
  private rateLimiter: RateLimiter;
  private queue: PQueue;
  private logger: Logger;
  
  // Services
  public timeCapsules: TimeCapsuleService;
  public payments: PaymentService;
  public legalDocuments: LegalDocumentService;
  public analytics: AnalyticsService;
  public ipfs: IPFSService;
  public crypto: CryptoService;
  
  // Web3 clients
  private publicClient: any;
  private walletClient: any;
  private provider?: ethers.Provider;
  private signer?: ethers.Signer;

  constructor(config: SDKConfig) {
    super();
    this.config = config;
    this.logger = new Logger(config.debug);
    
    // Initialize rate limiter
    const planType = this.getPlanTypeFromApiKey(config.apiKey);
    this.rateLimiter = new RateLimiter(planType);
    
    // Initialize HTTP client
    this.httpClient = axios.create({
      baseURL: this.getApiBaseUrl(),
      headers: {
        'X-API-Key': config.apiKey,
        'Content-Type': 'application/json'
      },
      timeout: 30000
    });
    
    // Add request interceptor for rate limiting
    this.httpClient.interceptors.request.use(
      async (config) => {
        const { allowed, retryAfter } = await this.rateLimiter.checkLimit(this.config.apiKey);
        if (!allowed) {
          throw new Error(`Rate limit exceeded. Retry after ${retryAfter} seconds`);
        }
        return config;
      },
      (error) => Promise.reject(error)
    );
    
    // Add response interceptor for error handling
    this.httpClient.interceptors.response.use(
      (response) => response,
      async (error) => {
        if (error.response?.status === 429) {
          this.emit('rateLimit:exceeded', this.rateLimiter.getRemainingLimits(this.config.apiKey));
        }
        return Promise.reject(error);
      }
    );
    
    // Initialize request queue
    this.queue = new PQueue({ 
      concurrency: planType === PlanType.ENTERPRISE ? 10 : 3,
      interval: 1000,
      intervalCap: planType === PlanType.ENTERPRISE ? 100 : 10
    });
    
    // Initialize Web3 clients
    this.initializeWeb3Clients();
    
    // Initialize services
    this.crypto = new CryptoService();
    this.ipfs = new IPFSService(config.ipfsGateway, config.pinataJwt);
    this.timeCapsules = new TimeCapsuleService(this);
    this.payments = new PaymentService(this);
    this.legalDocuments = new LegalDocumentService(this);
    this.analytics = new AnalyticsService(this);
    
    this.logger.log('LegacyChain SDK initialized');
  }

  private initializeWeb3Clients() {
    const chain = this.config.network === 'mainnet' ? mainnet : sepolia;
    const transport = this.config.rpcUrl 
      ? http(this.config.rpcUrl)
      : http();
    
    this.publicClient = createPublicClient({
      chain,
      transport
    });
    
    // Initialize ethers provider if needed
    if (this.config.rpcUrl) {
      this.provider = new ethers.JsonRpcProvider(this.config.rpcUrl);
    }
  }

  async connectWallet(ethereum: any) {
    if (!ethereum) {
      throw new Error('No Ethereum provider found');
    }
    
    const chain = this.config.network === 'mainnet' ? mainnet : sepolia;
    
    this.walletClient = createWalletClient({
      chain,
      transport: custom(ethereum)
    });
    
    // Also create ethers signer for compatibility
    const provider = new ethers.BrowserProvider(ethereum);
    this.signer = await provider.getSigner();
    
    this.logger.log('Wallet connected');
  }

  private getPlanTypeFromApiKey(apiKey: string): PlanType {
    // In a real implementation, this would validate the API key
    // and return the appropriate plan type from your backend
    if (apiKey.startsWith('ent_')) return PlanType.ENTERPRISE;
    if (apiKey.startsWith('pro_')) return PlanType.PRO;
    if (apiKey.startsWith('starter_')) return PlanType.STARTER;
    return PlanType.FREE;
  }

  private getApiBaseUrl(): string {
    switch (this.config.network) {
      case 'mainnet':
        return 'https://api.legacychain.net/v1';
      case 'testnet':
        return 'https://api-testnet.legacychain.net/v1';
      case 'devnet':
        return 'https://api-dev.legacychain.net/v1';
      default:
        return 'https://api.legacychain.net/v1';
    }
  }

  async makeRequest<T>(
    method: 'GET' | 'POST' | 'PUT' | 'DELETE',
    endpoint: string,
    data?: any,
    options?: any
  ): Promise<ApiResponse<T>> {
    return this.queue.add(async () => {
      try {
        const response = await this.httpClient.request<ApiResponse<T>>({
          method,
          url: endpoint,
          data,
          ...options
        });
        
        return response.data;
      } catch (error: any) {
        const apiError: ApiError = {
          code: error.response?.data?.error?.code || 'UNKNOWN_ERROR',
          message: error.response?.data?.error?.message || error.message,
          details: error.response?.data?.error?.details
        };
        
        this.emit('error', apiError);
        
        return {
          success: false,
          error: apiError,
          metadata: {
            timestamp: Date.now(),
            requestId: error.response?.headers?.['x-request-id'] || 'unknown'
          }
        };
      }
    });
  }

  // Getters for web3 clients
  getPublicClient() {
    return this.publicClient;
  }

  getWalletClient() {
    if (!this.walletClient) {
      throw new Error('Wallet not connected. Call connectWallet() first.');
    }
    return this.walletClient;
  }

  getProvider() {
    return this.provider;
  }

  getSigner() {
    if (!this.signer) {
      throw new Error('Wallet not connected. Call connectWallet() first.');
    }
    return this.signer;
  }

  // Rate limit info
  getRateLimits() {
    return this.rateLimiter.getRemainingLimits(this.config.apiKey);
  }

  // Configuration
  getConfig() {
    return { ...this.config };
  }

  updateConfig(newConfig: Partial<SDKConfig>) {
    this.config = { ...this.config, ...newConfig };
    
    // Update HTTP client if API key changed
    if (newConfig.apiKey) {
      this.httpClient.defaults.headers['X-API-Key'] = newConfig.apiKey;
      const newPlanType = this.getPlanTypeFromApiKey(newConfig.apiKey);
      this.rateLimiter.changePlan(newPlanType);
    }
  }

  // Cleanup
  destroy() {
    this.removeAllListeners();
    this.queue.clear();
    this.logger.log('LegacyChain SDK destroyed');
  }
} 